import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslocoModule } from '@jsverse/transloco';
import { CommunityService } from '../../services/community.service';
import { CommunityVersionSnapshot } from '../../models/community.models';
import { deferCommunityVideoEmbeds } from '../community-video-embed/community-video-embed';
import { CommunityVideoEmbedsDirective } from '../community-video-embed/community-video-embeds.directive';

@Component({
  selector: 'app-community-history',
  standalone: true,
  imports: [CommonModule, TranslocoModule, CommunityVideoEmbedsDirective],
  templateUrl: './community-history.component.html',
  styleUrl: './community-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityHistoryComponent implements OnInit {
  @Input({ required: true }) target!: 'thread' | 'post';
  @Input({ required: true }) targetId!: number;
  @Input() showTitle = false;

  versions = signal<CommunityVersionSnapshot[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);

  constructor(
    private communityService: CommunityService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const req$ = this.target === 'thread'
      ? this.communityService.getThreadVersions(this.targetId)
      : this.communityService.getPostVersions(this.targetId);

    req$.subscribe({
      next: res => {
        this.versions.set(res.versions || []);
        this.loading.set(false);
      },
      error: err => {
        this.error.set(err?.error?.error || 'Failed to load history');
        this.loading.set(false);
      }
    });
  }

  /**
   * Memoised per body: a fresh SafeHtml on every change detection would make
   * Angular re-set `innerHTML` — and put a video the reader just loaded back
   * behind its placeholder (the click itself triggers change detection).
   */
  private readonly safeCache = new Map<string, SafeHtml>();

  safe(html: string | undefined): SafeHtml {
    const key = html || '';
    let value = this.safeCache.get(key);
    if (!value) {
      // Video iframes become two-click placeholders here too (decision P9).
      value = this.sanitizer.bypassSecurityTrustHtml(deferCommunityVideoEmbeds(key));
      this.safeCache.set(key, value);
    }
    return value;
  }
}
